import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./dashboard.scss";
import BreadCrumsHeader from "../BreadCrums";
import Box from "./Box";
import MerchantIcon from "../../../Assets/Icons/merchantIcon.svg";
import FundraiserIcon from "../../../Assets/Icons/FundraiserIcon.svg";
import MembershipIcon from "../../../Assets/Icons/membershipIcon.svg";
import CustomerIcon from "../../../Assets/Icons/customerIcon.svg";
import OfferIcon from "../../../Assets/Icons/offerIcon.svg";
import Summary from "./Summary";
// import MerchantOffer from "./MerchantOffer";
import { useDispatch, useSelector } from "react-redux";
import { roleEnum } from "../../../utils/utilFunctions";
import { getOnboardingStatus, getFundraiserProfile } from "../../../services/fundraiserService";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import { getAdminFundStats, getAdminFundBreakdown, getAdminOfferStats, getMerchantStatusSummary, getFundraiserStatusSummary } from "../../../services/adminService";
import AdminDetailDashboard from "./AdminDetailDashboard";
import FundraiserDashboard from "./FundraiserDashboard";
import Frequency from "./Frequency";
import NonRenewedSubscriptions from "./NonRenewedSubscriptions";
import UpcomingRenewals from "./UpcomingRenewals";
import { getDashboardStats } from "../../../services/subscriptionService";
import FundsChart from "./FundsChart";
import FundsTable from "./FundsTable";
import ActiveInactiveChart from "./ActiveInactiveChart";
import Loader from "../Loader";
import DetailsModal from "../DetailsModal";
import { clearAuth } from "../../../redux/slices/authSlice";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import { clearClientStorage } from "../../../utils/sessionManager";

const normalizeMerchantStatus = (business) =>
  String(business?.status || "").toLowerCase();

const isBlockedMerchantStatus = (business) =>
  ["disabled", "deleted", "declined", "rejected"].includes(normalizeMerchantStatus(business));

const getMerchantId = (business) => business?._id || business?.id || null;

const getBlockedMerchantMessage = (business) => {
  const status = normalizeMerchantStatus(business);
  if (status === "deleted") {
    return "Your merchant account has been deleted. Please contact support.";
  }
  return "Your merchant account has been disabled. Please contact support.";
};

const getFallbackMerchant = (merchantList = [], currentMerchant = null) => {
  const list = Array.isArray(merchantList) ? merchantList : [];
  return (
    list.find(
      (merchant) =>
        !isBlockedMerchantStatus(merchant) &&
        normalizeMerchantStatus(merchant) === "approved"
    ) ||
    list.find(
      (merchant) =>
        !isBlockedMerchantStatus(merchant) &&
        getMerchantId(merchant) !== getMerchantId(currentMerchant)
    ) ||
    null
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const fundraiserLoading = useSelector((state) => state.fundraiserReducer.loading);
  const { merchantDashboardStats, selectedMerchent } = useSelector(
    (state) => state?.merchantReducer
  );
  const merchantProfilesFromRedux = useSelector(
    (state) => state?.merchantReducer?.merchantProfiles
  );
  const userData = useSelector((state) => state?.authReducer?.tokenData?.user);
  const userRole = (userData?.role || "").toString().toLowerCase();
  const location = useLocation();
  const currentPath = location.pathname;
  const isAdminPath = useMemo(
    () =>
      !currentPath.includes(roleEnum.merchant) &&
      !currentPath.includes(roleEnum.fundraiser),
    [currentPath]
  );
  // Start with checking=true for fundraisers to prevent dashboard flash
  const [isCheckingStatus, setIsCheckingStatus] = useState(userRole === "fundraiser" && currentPath.includes(roleEnum.fundraiser));

  const merchatCardsData = merchantDashboardStats?.data;
  const [selectedFrequency, setSelectedFrequency] = useState("Monthly");
  const [freqLoading, setFreqLoading] = useState(false);
  const [merchantAccessModal, setMerchantAccessModal] = useState(null);
  const hasFetchedDashboardRef = React.useRef(false);
  const handledBlockedMerchantRef = React.useRef(false);
  const merchantStatusSummary = useSelector((s) => s?.adminReducer?.merchantStatusSummary);
  const fundraiserStatusSummary = useSelector((s) => s?.adminReducer?.fundraiserStatusSummary);
  const merchantProfileList = useMemo(
    () => merchantProfilesFromRedux?.data || userData?.merchantProfiles || [],
    [merchantProfilesFromRedux?.data, userData?.merchantProfiles]
  );

  const handleMerchantAccessClose = async () => {
    const shouldLogout = merchantAccessModal?.shouldLogout === true;
    setMerchantAccessModal(null);

    if (shouldLogout) {
      try {
        await dispatch(logout({ fcmToken: getFcmToken() })).unwrap();
      } catch (error) {
        console.warn("Merchant logout failed while handling blocked account:", error);
      } finally {
        dispatch(clearAuth());
        clearClientStorage();
        localStorage.removeItem("swoodle_selected_merchant_id");
        navigate("/", { replace: true });
      }
    }
  };
  const normalizeTimeframe = useCallback((freq) => {
    const value = String(freq || "").toLowerCase();
    const map = {
      today: "today",
      daily: "today",
      day: "today",
      weekly: "week",
      week: "week",
      monthly: "month",
      month: "month",
      yearly: "year",
      year: "year",
    };
    return map[value] || value;
  }, []);

  // Guard: check fundraiser approval status BEFORE any rendering
  useEffect(() => {
    const checkFundraiserStatus = async () => {
      if (userRole === "fundraiser" && currentPath.includes(roleEnum.fundraiser)) {
        // Check if already verified during login - skip re-checking
        const alreadyVerified = sessionStorage.getItem('fundraiser_verified') === 'true';

        if (alreadyVerified) {
          setIsCheckingStatus(false);
          return;
        }

        // Keep checking state true until verification complete
        try {
          const profileResponse = await getFundraiserProfile();

          const fundraiserProfile = profileResponse?.data;
          const profileStatusRaw = fundraiserProfile?.status;
          const profileStatus = typeof profileStatusRaw === "string" 
            ? profileStatusRaw.toLowerCase() 
            : profileStatusRaw;

          // Allow both "approved" and "live" statuses (matching login logic)
          const isApproved = profileStatus === "approved" || profileStatus === "live";

          if (!isApproved) {
            // NOT APPROVED - set flag for modal and logout
            // Keep loader showing during entire logout process
            const statusLower = String(profileStatus || "").toLowerCase();
            if (["disabled", "deleted", "rejected", "declined"].includes(statusLower)) {
              sessionStorage.setItem('fundraiser_block_status', statusLower);
              if (["rejected", "declined"].includes(statusLower)) {
                sessionStorage.setItem('showAccountPendingModal', 'true');
              }
            } else {
              // Pending/in-progress: show pending modal/message
              sessionStorage.setItem('showAccountPendingModal', 'true');
              sessionStorage.setItem('fundraiser_block_status', 'pending');
            }
            await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
            const msg =
              statusLower === 'disabled'
                ? 'Your account is disabled. Please contact support.'
                : statusLower === 'deleted'
                  ? 'Your account have been deleted. Please Contact Support'
                : statusLower === 'rejected'
                  ? 'Your account has been rejected. Please contact support.'
                  : statusLower === 'declined'
                    ? 'Your account has been declined. Please contact support.'
                    : 'Your account is pending admin approval. Please wait for approval.';
            navigate('/fundraiser/account-login', { replace: true, state: { message: msg } });
            return;
          }
          // Approved - allow dashboard to render
          setIsCheckingStatus(false);
        } catch (profileError) {
          await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
          navigate('/fundraiser/account-login', { replace: true, state: { message: 'Failed to verify account status. Please try again.' } });
          return;
        }
      } else {
        // Not fundraiser or not fundraiser path - don't check
        setIsCheckingStatus(false);
      }
    };
    checkFundraiserStatus();
  }, []); // Empty deps array - only run once on mount

  const getSeries = (obj) => (Array.isArray(obj) ? obj : null);
  const normalize12 = (arr) => {
    if (!arr) return undefined;
    const nums = arr.map((n) => Number(n) || 0);
    return nums.length === 12 ? nums : [...nums, ...new Array(12 - nums.length).fill(0)].slice(0, 12);
  };

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentYearKey = String(new Date().getFullYear());
  const monthKeyMap = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
  };

  const buildSeriesFromStatus = (statusObj) => {
    const s = statusObj || {};
    const scoped =
      s?.[currentYearKey] ||
      s?.yearly?.[currentYearKey] ||
      s?.yearlyBreakdown?.[currentYearKey] ||
      s?.byYear?.[currentYearKey] ||
      s?.perYear?.[currentYearKey] ||
      s?.data?.[currentYearKey] ||
      s;
    // Direct arrays
    const activeArr = getSeries(scoped?.active) || getSeries(scoped?.monthly?.active) || getSeries(scoped?.perMonth?.active);
    const inactiveArr = getSeries(scoped?.inactive) || getSeries(scoped?.monthly?.inactive) || getSeries(scoped?.perMonth?.inactive);
    if (activeArr || inactiveArr) {
      return {
        active: normalize12(activeArr || Array(12).fill(0)),
        inactive: normalize12(inactiveArr || Array(12).fill(0)),
      };
    }

    // Monthly object keyed by month name → counts
    if (scoped?.monthly && typeof scoped.monthly === 'object' && !Array.isArray(scoped.monthly)) {
      const a = Array(12).fill(0);
      const i = Array(12).fill(0);
      Object.entries(scoped.monthly).forEach(([k, v]) => {
        const idx = monthKeyMap[String(k).toLowerCase()];
        if (idx != null) {
          a[idx] = Number(v?.activeMerchants ?? v?.activeFundraisers ?? v?.active) || 0;
          i[idx] = Number(v?.inactiveMerchants ?? v?.inactiveFundraisers ?? v?.inactive) || 0;
        }
      });
      return { active: a, inactive: i };
    }

    // Monthly array of objects [{ month: 'August', activeMerchants, inactiveMerchants }, ...]
    if (Array.isArray(scoped?.monthly)) {
      const a = Array(12).fill(0);
      const i = Array(12).fill(0);
      scoped.monthly.forEach((row) => {
        const idx = monthKeyMap[String(row?.month).toLowerCase()];
        if (idx != null) {
          a[idx] = Number(row?.activeMerchants ?? row?.activeFundraisers ?? row?.active) || 0;
          i[idx] = Number(row?.inactiveMerchants ?? row?.inactiveFundraisers ?? row?.inactive) || 0;
        }
      });
      return { active: a, inactive: i };
    }

    // Weekly object: sum and place at last column
    if (scoped?.weekly && typeof scoped.weekly === 'object') {
      let active = 0, inactive = 0;
      Object.values(scoped.weekly).forEach((v) => {
        active += Number(v?.activeMerchants ?? v?.activeFundraisers ?? v?.active) || 0;
        inactive += Number(v?.inactiveMerchants ?? v?.inactiveFundraisers ?? v?.inactive) || 0;
      });
      const a = Array(12).fill(0); a[11] = active;
      const i = Array(12).fill(0); i[11] = inactive;
      return { active: a, inactive: i };
    }

    // Today only: place at last column
    if (scoped?.today) {
      const a = Array(12).fill(0); a[11] = Number(scoped.today?.activeMerchants ?? scoped.today?.activeFundraisers ?? scoped.today?.active) || 0;
      const i = Array(12).fill(0); i[11] = Number(scoped.today?.inactiveMerchants ?? scoped.today?.inactiveFundraisers ?? scoped.today?.inactive) || 0;
      return { active: a, inactive: i };
    }

    return { active: Array(12).fill(0), inactive: Array(12).fill(0) };
  };

  const merchantsActiveInactive = buildSeriesFromStatus(merchantStatusSummary);

  const fundraisersActiveInactive = buildSeriesFromStatus(fundraiserStatusSummary);

  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant)) return;
    if (handledBlockedMerchantRef.current) return;
    if (!isBlockedMerchantStatus(selectedMerchent)) return;

    handledBlockedMerchantRef.current = true;
    const fallbackMerchant = getFallbackMerchant(merchantProfileList, selectedMerchent);
    setMerchantAccessModal({
      message: getBlockedMerchantMessage(selectedMerchent),
      shouldLogout: !fallbackMerchant,
    });

    if (fallbackMerchant) {
      dispatch(setCurrentBusiness(fallbackMerchant));
    }
  }, [currentPath, selectedMerchent, merchantProfileList, dispatch]);

  // Consolidated dashboard fetch with single in-flight guard
  const inFlightAllRef = React.useRef(false);
  const pendingTimeframeRef = React.useRef(null);
  // Fetch all dashboard data
  const fetchAll = useCallback(async (timeframe) => {
    if (currentPath.includes(roleEnum.merchant) && isBlockedMerchantStatus(selectedMerchent)) {
      return;
    }

    const tf = normalizeTimeframe(timeframe) || undefined;
    if (inFlightAllRef.current) {
      pendingTimeframeRef.current = tf;
      return;
    }
    inFlightAllRef.current = true;
    if (hasFetchedDashboardRef.current) {
      setFreqLoading(true);
    }
    try {
      const currentYear = new Date().getFullYear();
      const [statsRes] = await Promise.all([
        dispatch(getDashboardStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminOfferStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminFundStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminFundBreakdown(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getMerchantStatusSummary({ timeframe: tf || 'year', year: currentYear, currentYear }))?.unwrap(),
        dispatch(getFundraiserStatusSummary({ timeframe: tf || 'year', year: currentYear, currentYear }))?.unwrap(),
      ]);

      if (statsRes?.success) setAdminStats(statsRes?.data);
    } catch (_) {
    } finally {
      hasFetchedDashboardRef.current = true;
      inFlightAllRef.current = false;
      setFreqLoading(false);
      const pendingTimeframe = pendingTimeframeRef.current;
      pendingTimeframeRef.current = null;
      if (pendingTimeframe && pendingTimeframe !== tf) {
        fetchAll(pendingTimeframe);
      }
    }
  }, [dispatch, normalizeTimeframe]);

  // Deterministic admin fetch:
  // run on initial mount, route-entry, and frequency changes.
  useEffect(() => {
    if (!isAdminPath) return;
    const tf = selectedFrequency?.toLowerCase?.();
    fetchAll(tf);
  }, [isAdminPath, selectedFrequency, selectedMerchent, location.key, fetchAll, currentPath, userRole]);

  // Listen for offer creation/updates to refresh dashboard data
  const createdOffer = useSelector((state) => state?.offerReducer?.createdOffer);
  const updatedOffer = useSelector((state) => state?.offerReducer?.updatedOffer);

  useEffect(() => {
    if (createdOffer || updatedOffer) {
      if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }
    }
  }, [createdOffer, updatedOffer, currentPath, selectedFrequency, fetchAll]);

  const items = [
    {
      title: "Welcome to Swoodle",
    },
  ];
  // Admin summary cards — ordered and named to match the provided design
  const [adminStats, setAdminStats] = useState(null);
  const adminBoxes = [
    {
      text: "Total Subscriptions",
      number: pickTotal(adminStats, "subscriptions.total", "totalSubscriptions", "subscriptions", "totals.subscriptions"),
      icon: MembershipIcon,
    },
    {
      text: "Total Fundraisers",
      number: pickTotal(adminStats, "fundraisers.total", "totalFundraisers", "fundraisers", "totals.fundraisers"),
      icon: FundraiserIcon,
    },
    {
      text: "Total Merchants",
      number: pickTotal(adminStats, "merchants.total", "totalMerchants", "merchants", "totals.merchants"),
      icon: MerchantIcon,
    },
    {
      text: "Total Offers",
      number: pickTotal(adminStats, "offers.total", "totalOffers", "offers", "totals.offers"),
      icon: OfferIcon,
    },
  ];

  const merchantBoxes = [
    {
      // text: "Total Merchants",
      text: "Unique Users",
      number: merchatCardsData?.uniqueUsers,
      icon: MerchantIcon,
    },
    {
      // text: "Total Offers",
      text: "Total Offers",
      number: merchatCardsData?.totalOffers,
      icon: OfferIcon,
    },
    {
      // text: "Active Merchants",
      text: "Total Redemptions",
      number: merchatCardsData?.totalRedumption,
      icon: CustomerIcon,
    },
    // {
    //   text: "Inactive Merchants",
    //   text: "Total Revenue",
    //   number: merchatCardsData?.totalRevenue,
    //   icon: InactiveMerchantsIcon,
    // },
  ];

  const frequencies = [
    "Monthly",
    "Weekly",
    "Yearly",
    "Today"
  ];

  const getStatus = async () => {
    // Skip check if already verified during login
    const alreadyVerified = sessionStorage.getItem('fundraiser_verified') === 'true';

    if (alreadyVerified) {
      return;
    }

    try {
      const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
      const data = statusPromise?.data;

      // If we don't get valid data, don't make any navigation decisions
      if (!data) return;

      if (
        data.detailsSubmitted === true &&
        data.chargesEnabled === false
      ) {
        if (currentPath !== "/complete-setup") {
          navigate("/complete-setup");
        }
      } else if (
        data.detailsSubmitted === false &&
        data.chargesEnabled === false
      ) {
        if (currentPath !== "/fundraiser/welcome") {
          navigate("/fundraiser/welcome");
        }
      }
    } catch (error) {
      // During network errors, don't redirect the user. Stay on current page.
    }
  };

  useEffect(() => {
    if (currentPath.includes(roleEnum.fundraiser)) {
      getStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);



  useEffect(() => {
    // Expose selected frequency globally for lightweight consumers (e.g., NewOffers tile)
    if (typeof window !== "undefined") {
      window.__ADMIN_SELECTED_FREQUENCY__ = selectedFrequency;
    }
  }, [selectedFrequency]);

  // Refresh dashboard data when user returns to dashboard
  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const isReturningFromOfferFlow = sessionStorage.getItem("ADMIN_OFFER_CREATION_GUARD");
      if (isReturningFromOfferFlow) {
        sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }
    }
  }, [currentPath, selectedFrequency, fetchAll]);

  // Optional: gentle periodic refresh (5 min)
  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const refreshInterval = setInterval(() => {
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }, 300000); // 5 minutes
      return () => clearInterval(refreshInterval);
    }
  }, [currentPath, selectedFrequency, fetchAll]);

  // For fundraisers: Return ONLY full-screen loader while checking status
  // This prevents sidebar/navbar from rendering (no dashboard flash)
  if (currentPath.includes(roleEnum.fundraiser) && isCheckingStatus) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {merchantAccessModal && currentPath.includes(roleEnum.merchant) && (
        <DetailsModal
          handleClose={handleMerchantAccessClose}
          content={
            <div className="px-2 py-1 sm:px-5 sm:py-3">
              <p className="max-w-[23rem] text-center text-[15px] sm:text-[16px] leading-6 font-poppins text-[#303030]">
                {merchantAccessModal.message}
              </p>
            </div>
          }
          popupClassName="p-6 w-[340px] sm:w-[460px] md:w-[500px]"
          continueBtn={
            <button
              type="button"
              onClick={handleMerchantAccessClose}
              className="h-[40px] w-[128px] rounded-[6px] bg-[#00ADEF] text-white font-poppins font-[500]"
            >
              OK
            </button>
          }
        />
      )}
      {currentPath.includes(roleEnum.merchant) ? (
        <>
          <BreadCrumsHeader heading="Dashboard" items={items} form={true} />
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 w-full gap-3 my-[2%]">
            {merchantBoxes?.map((b) => (
              <Box
                text={b?.text}
                number={b?.number}
                icon={b?.icon}
                mainClassName="box p-3 h-[113px]"
                textClassName="text-[14px] font-poppins font-[600] text-[white]"
                numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
              />
            ))}
          </div>
                <Summary key={selectedMerchent?._id || 'no-merchant'} selectedFrequency={selectedFrequency} />
          {/* <div className="w-full flex sm:flex-col lg:flex-row mt-16 justify-between items-stretch gap-4">
            <div className="sm:w-full lg:w-[60%]">
              <Summary />
            </div>
            <div className="sm:w-full lg:w-[40%]">
              <OffersCategory />
            </div>
          </div> */}
        </>
      ) : currentPath.includes(roleEnum.fundraiser) ? (
        // NEVER render dashboard until status check complete - prevents flash
        isCheckingStatus ? <Loader /> : <FundraiserDashboard items={items} />
      ) : (
        <>
          {freqLoading && <Loader />}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center col-span-12 gap-4">
            <BreadCrumsHeader heading="Dashboard" items={items} form={true} />
            <Frequency
              selectedFrequency={selectedFrequency}
              setSelectedFrequency={(val) => {
                setSelectedFrequency(val);
                if (typeof window !== 'undefined') { window.__ADMIN_SELECTED_FREQUENCY__ = val; }
                const tf = val?.toLowerCase?.();
                fetchAll(tf);
              }}
              frequencies={frequencies}
              className='frequency w-full sm:w-[50%] md:w-[50%] lg:w-[40%] xl:w-[30%] py-2 px-[1%]'
            />
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full gap-3 my-[2%]">
            {adminBoxes?.map((b) => (
              <Box
                text={b?.text}
                number={b?.number}
                icon={b?.icon}
                reverse
                mainClassName="box p-3 h-[113px]"
                textClassName="text-[14px] font-poppins font-[600] text-[white]"
                numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
              />
            ))}
          </div>
          {/* In-widget loader removed as per request */}
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <NonRenewedSubscriptions selectedFrequency={selectedFrequency} />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <UpcomingRenewals selectedFrequency={selectedFrequency} />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <FundsChart />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <FundsTable />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <ActiveInactiveChart
                title="Active & Inactive Merchants"
                viewAllLink="/admin/merchant-list"
                colors={['#91CC75', '#00ADEF']}
                data={merchantsActiveInactive}
              />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <ActiveInactiveChart
                title="Active & Inactive Fundraisers"
                viewAllLink="/admin/fundraiser-list"
                colors={['#91CC75', '#5470C6']}
                data={fundraisersActiveInactive}
              />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch">
            <div className="sm:w-full lg:w-full">
              <AdminDetailDashboard selectedFrequency={selectedFrequency} />
            </div>
            {/* <div className="sm:w-full lg:w-[49.5%]">
              <MerchantOffer />
            </div> */}
          </div>
        </>
      )}
    </>
  );
};

export default AdminDashboard;
  const pickTotal = (value, ...paths) => {
    for (const path of paths) {
      const result = path.split('.').reduce((acc, key) => acc?.[key], value);
      if (result !== undefined && result !== null) {
        return result;
      }
    }
    return "-";
  };
